import { CheckCircleFilled, CloseCircleFilled, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Empty, Form, Input, Popconfirm, Row, Select, Table, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation, useDictionaries } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';

import { SectionHeader, SidePanelLayout } from './SidePanel';
import type { EmployeeDetail } from './types';

const fmtDate = (value: string | null | undefined): string =>
  value ? dayjs(value).format('DD.MM.YYYY') : '—';

/** «Осталось дней»: красным — просрочен, оранжевым — меньше месяца. */
function DaysLeft({ expiresAt }: { expiresAt: string | null }) {
  const { t } = useTranslation();
  if (!expiresAt) return <Typography.Text type="secondary">{t('бессрочно')}</Typography.Text>;
  const days = dayjs(expiresAt).startOf('day').diff(dayjs().startOf('day'), 'day');
  const type = days < 0 ? 'danger' : days <= 30 ? 'warning' : undefined;
  return <Typography.Text type={type} strong={days <= 30}>{days}</Typography.Text>;
}

function Valid({ expiresAt }: { expiresAt: string | null }) {
  const { t } = useTranslation();
  const valid = !expiresAt || !dayjs(expiresAt).isBefore(dayjs(), 'day');
  return valid ? (
    <Tooltip title={t('Действует')}>
      <CheckCircleFilled style={{ color: '#52c41a' }} />
    </Tooltip>
  ) : (
    <Tooltip title={t('Просрочен')}>
      <CloseCircleFilled style={{ color: '#ff4d4f' }} />
    </Tooltip>
  );
}

type Adding = 'permit' | 'document' | null;

/** Допуски и документы личного дела — со сроками, видно, что истекает. */
export function DocumentsSection({ employee }: { employee: EmployeeDetail }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const dictionaries = useDictionaries();
  const [adding, setAdding] = useState<Adding>(null);
  const [form] = Form.useForm();

  const canPermits = can(PERMISSIONS.EMPLOYEE_PERMIT_MANAGE);
  const canDocuments = can(PERMISSIONS.EMPLOYEE_DOCUMENT_MANAGE);
  const invalidate = [['employee'], ['employees'], ['drivers'], ['driver'], ['expiring']];

  const add = useApiMutation(
    async (values: Record<string, unknown>) => {
      const date = (v: unknown) => (v ? (v as dayjs.Dayjs).format('YYYY-MM-DD') : undefined);
      const url = adding === 'permit' ? 'permits' : 'documents';
      return (
        await api.post(`/employees/${employee.id}/${url}`, {
          ...values,
          issuedAt: date(values.issuedAt),
          expiresAt: date(values.expiresAt),
        })
      ).data;
    },
    { successMessage: t('Сохранено'), invalidate },
  );

  const removePermit = useApiMutation(
    async (id: number) => (await api.delete(`/employees/${employee.id}/permits/${id}`)).data,
    { successMessage: t('Допуск удалён'), invalidate },
  );
  const removeDocument = useApiMutation(
    async (id: number) => (await api.delete(`/employees/${employee.id}/documents/${id}`)).data,
    { successMessage: t('Документ удалён'), invalidate },
  );

  const open = (kind: Exclude<Adding, null>): void => {
    form.resetFields();
    setAdding(kind);
  };

  const deleteButton = (title: string, onConfirm: () => void) => (
    <Popconfirm title={title} okText={t('Удалить')} cancelText={t('Отмена')} onConfirm={onConfirm}>
      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
    </Popconfirm>
  );

  const panel = adding && (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => add.mutate(values, { onSuccess: () => setAdding(null) })}
    >
      {adding === 'permit' ? (
        <Form.Item
          name="permitTypeId"
          label={t('Тип допуска')}
          rules={[{ required: true, message: t('Обязательное поле') }]}
        >
          <Select
            options={(dictionaries.data?.permitTypes ?? []).map((type) => ({ value: type.id, label: type.name }))}
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="documentTypeId"
          label={t('Тип документа')}
          rules={[{ required: true, message: t('Обязательное поле') }]}
        >
          <Select
            options={(dictionaries.data?.documentTypes ?? []).map((type) => ({ value: type.id, label: type.name }))}
          />
        </Form.Item>
      )}
      <Form.Item name="documentNumber" label={t('Номер документа')}>
        <Input />
      </Form.Item>
      {adding === 'document' && (
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item name="issuedCountry" label={t('Страна выдачи')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="issuedBy" label={t('Кем выдан')}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item
            name="issuedAt"
            label={t('Выдан')}
            rules={adding === 'permit' ? [{ required: true, message: t('Обязательное поле') }] : []}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="expiresAt"
            label={t('Действует до')}
            tooltip={adding === 'document' ? t('Пусто — документ бессрочный') : undefined}
            rules={adding === 'permit' ? [{ required: true, message: t('Обязательное поле') }] : []}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
      </Row>
      {adding === 'document' && (
        <Form.Item name="notes" label={t('Примечание')}>
          <Input.TextArea rows={2} />
        </Form.Item>
      )}
      <Button type="primary" htmlType="submit" block loading={add.isPending}>
        {t('Сохранить')}
      </Button>
    </Form>
  );

  return (
    <SidePanelLayout
      panel={panel}
      title={adding === 'permit' ? t('Новый допуск') : t('Новый документ')}
      onClose={() => setAdding(null)}
    >
      <SectionHeader
        title={t('Допуски')}
        extra={
          canPermits && (
            <Button size="small" icon={<PlusOutlined />} onClick={() => open('permit')}>
              {t('Добавить допуск')}
            </Button>
          )
        }
      />
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={employee.permits}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Нет допусков')} /> }}
        columns={[
          { title: t('Тип допуска'), render: (_, row) => row.permitType.name },
          { title: t('Номер'), dataIndex: 'documentNumber', render: (v: string | null) => v ?? '—' },
          { title: t('Выдан'), dataIndex: 'issuedAt', width: 110, render: fmtDate },
          { title: t('Действует до'), dataIndex: 'expiresAt', width: 120, render: fmtDate },
          {
            title: t('Осталось, дн.'),
            width: 110,
            align: 'right',
            render: (_, row) => <DaysLeft expiresAt={row.expiresAt} />,
          },
          { title: t('Действ.'), width: 70, align: 'center', render: (_, row) => <Valid expiresAt={row.expiresAt} /> },
          ...(canPermits
            ? [
                {
                  title: '',
                  width: 48,
                  render: (_: unknown, row: { id: number }) =>
                    deleteButton(t('Удалить допуск?'), () => removePermit.mutate(row.id)),
                },
              ]
            : []),
        ]}
      />

      <div style={{ height: 24 }} />
      <SectionHeader
        title={t('Документы личного дела')}
        extra={
          canDocuments && (
            <Button size="small" icon={<PlusOutlined />} onClick={() => open('document')}>
              {t('Добавить документ')}
            </Button>
          )
        }
      />
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={employee.documents}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Нет документов')} /> }}
        expandable={{
          rowExpandable: (row) => Boolean(row.notes),
          expandedRowRender: (row) => <Typography.Text type="secondary">{row.notes}</Typography.Text>,
        }}
        columns={[
          { title: t('Тип документа'), render: (_, row) => row.documentType.name },
          { title: t('Номер'), dataIndex: 'documentNumber', render: (v: string | null) => v ?? '—' },
          {
            title: t('Кем выдан'),
            render: (_, row) => [row.issuedBy, row.issuedCountry].filter(Boolean).join(', ') || '—',
          },
          { title: t('Выдан'), dataIndex: 'issuedAt', width: 110, render: fmtDate },
          { title: t('Действует до'), dataIndex: 'expiresAt', width: 120, render: fmtDate },
          {
            title: t('Осталось, дн.'),
            width: 110,
            align: 'right',
            render: (_, row) => <DaysLeft expiresAt={row.expiresAt} />,
          },
          { title: t('Действ.'), width: 70, align: 'center', render: (_, row) => <Valid expiresAt={row.expiresAt} /> },
          ...(canDocuments
            ? [
                {
                  title: '',
                  width: 48,
                  render: (_: unknown, row: { id: number }) =>
                    deleteButton(t('Удалить документ?'), () => removeDocument.mutate(row.id)),
                },
              ]
            : []),
        ]}
      />
    </SidePanelLayout>
  );
}
